import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  inventoryUrl, salesUrl, saleByIdUrl, customersUrl, ASSETS,
} from '../../BaseURL';
import Modal from '../../components/Modal';
import Receipt from '../../components/Receipt';
import BarcodeScannerModal from '../../components/BarcodeScannerModal';
import { useFacility } from '../../context/FacilityContext';
import { fmtMoney } from '../../utils/format';
import { swalError, swalWarning, toastSuccess } from '../../utils/swal';
import { defaultImageFor } from '../../utils/productImage';
import { publishCart, publishIdle, publishThankYou } from '../../utils/customerDisplay';
import '../../styles/pos.css';

const PAYMENT_METHODS = [
  { key: 'cash', label: 'Cash',         icon: '💵', color: 'cash' },
  { key: 'momo', label: 'Mobile Money', icon: '📱', color: 'momo' },
  { key: 'card', label: 'Card',         icon: '💳', color: 'card' },
  { key: 'bank', label: 'Bank',         icon: '🏦', color: 'bank' },
];

// Common cash denominations / round-up buttons. Picking one adds that amount
// to the active cash row — saves the cashier from typing.
const QUICK_CASH = [5, 10, 20, 50, 100, 200];

const POS = () => {
  const { facility } = useFacility();
  const [me] = useState(() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; } });
  const [inventory, setInventory] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [customerId, setCustomerId] = useState('');
  const [payments, setPayments] = useState([{ method: 'cash', amount: '' }]);
  const [activePay, setActivePay] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [lastSale, setLastSale] = useState(null);
  const [bumpedId, setBumpedId] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const searchRef = useRef(null);

  const load = async () => {
    const [inv, cust] = await Promise.all([
      axios.get(inventoryUrl), axios.get(customersUrl),
    ]);
    setInventory(inv.data?.results || []);
    setCustomers(cust.data?.results || []);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  // Keyboard shortcuts: ⌘K / Ctrl-K focuses search, F2 opens camera scanner,
  // F9 attempts checkout, Esc clears the active input. USB barcode scanners
  // type into whichever input is focused — so the search field handles them.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault(); searchRef.current?.focus();
      }
      if (e.key === 'F2') {
        e.preventDefault(); setScannerOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const categories = useMemo(() => {
    const set = new Map();
    inventory.forEach((p) => {
      const c = p.category_name || 'Uncategorized';
      set.set(c, (set.get(c) || 0) + 1);
    });
    return [['all', inventory.length], ...Array.from(set.entries()).sort((a, b) => b[1] - a[1])];
  }, [inventory]);

  const filtered = useMemo(() => {
    let base = inventory;
    if (activeCategory !== 'all') {
      base = base.filter((p) => (p.category_name || 'Uncategorized') === activeCategory);
    }
    if (!search.trim()) return base;
    const q = search.trim().toLowerCase();
    const exact = inventory.find((p) =>
      String(p.barcode || '').toLowerCase() === q ||
      String(p.sku || '').toLowerCase() === q
    );
    if (exact) return [exact];
    return base.filter((p) =>
      [p.name, p.sku, p.barcode, p.category_name].some((v) => String(v || '').toLowerCase().includes(q))
    );
  }, [inventory, search, activeCategory]);

  // Cart map keyed by product_id so tiles can show their "in cart" badge.
  const cartQty = useMemo(() => {
    const m = new Map();
    cart.forEach((r) => m.set(r.product_id, r.qty));
    return m;
  }, [cart]);

  const addToCart = (p) => {
    if (Number(p.qty_available) <= 0) return;
    setBumpedId(p.product_id);
    setTimeout(() => setBumpedId(null), 350);
    setCart((prev) => {
      const idx = prev.findIndex((r) => r.product_id === p.product_id);
      if (idx >= 0) {
        const next = [...prev];
        if (next[idx].qty + 1 > Number(p.qty_available)) return next;
        next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
        return next;
      }
      return [...prev, {
        product_id: p.product_id, name: p.name, qty: 1,
        unit_price: Number(p.effective_price ?? p.selling_price), tax_percent: 0,
        qty_available: Number(p.qty_available), image: p.image,
        unit: p.unit, category_name: p.category_name,
      }];
    });
  };

  // Look up by exact barcode/SKU and add to cart. Used by both the camera
  // scanner and the Enter key on the search input.
  const handleScannedCode = (code) => {
    const c = String(code).trim().toLowerCase();
    if (!c) return;
    const hit = inventory.find((p) =>
      String(p.barcode || '').toLowerCase() === c ||
      String(p.sku || '').toLowerCase() === c
    );
    if (hit) {
      addToCart(hit);
      toastSuccess(`Scanned: ${hit.name}`);
      setSearch('');
    } else {
      swalWarning('Not found', `No product matches "${code}". Check the barcode or SKU.`);
    }
  };

  const setQty = (id, qty) => {
    setCart((prev) => prev.map((r) => r.product_id === id
      ? { ...r, qty: Math.max(0, Math.min(Number(qty) || 0, r.qty_available)) }
      : r
    ).filter((r) => r.qty > 0));
  };

  const removeRow = (id) => setCart((prev) => prev.filter((r) => r.product_id !== id));

  const subtotal = useMemo(() => cart.reduce((s, r) => s + r.qty * r.unit_price, 0), [cart]);
  const total = Math.max(0, subtotal - Number(discount || 0));
  const paid = useMemo(() => payments.reduce((s, p) => s + (Number(p.amount) || 0), 0), [payments]);
  const change = Math.max(0, paid - total);
  const due = Math.max(0, total - paid);
  const cartCount = useMemo(() => cart.reduce((s, r) => s + r.qty, 0), [cart]);

  // Push the current cart snapshot to the customer-facing display window.
  // Any window pointed at /customer-display picks this up over BroadcastChannel.
  useEffect(() => {
    if (cart.length === 0) {
      publishIdle();
      return;
    }
    publishCart({
      kind: 'cart',
      items: cart.map((r) => ({
        product_id: r.product_id,
        name: r.name,
        qty: r.qty,
        unit_price: r.unit_price,
        line_total: r.qty * r.unit_price,
        unit: r.unit,
      })),
      subtotal, discount: Number(discount) || 0, total,
      paid, change, due, cartCount,
      facility: facility ? { name: facility.name, logo: facility.logo } : null,
      branchName: me?.branch_name || null,
      cashier: me?.full_name || null,
    });
  }, [cart, discount, subtotal, total, paid, change, due, cartCount, facility, me]);

  const setPayment = (i, patch) => setPayments((prev) => prev.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  const addPayment = () => {
    setPayments((prev) => [...prev, { method: 'cash', amount: '' }]);
    setActivePay(payments.length);
  };
  const removePayment = (i) => {
    setPayments((prev) => prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i));
    setActivePay(0);
  };

  // Quick cash chip handler — adds the chip's amount to the active payment row,
  // and "Exact" sets it to the exact remaining due.
  const bumpCash = (amount) => {
    setPayments((prev) => prev.map((p, idx) => {
      if (idx !== activePay) return p;
      const next = (Number(p.amount) || 0) + amount;
      return { ...p, amount: String(next) };
    }));
  };
  const setExact = () => {
    setPayments((prev) => prev.map((p, idx) =>
      idx === activePay ? { ...p, amount: String(Math.max(0, due > 0 ? (Number(p.amount) || 0) + due : total)) } : p
    ));
  };

  const reset = () => {
    setCart([]); setDiscount(0); setCustomerId('');
    setPayments([{ method: 'cash', amount: '' }]); setActivePay(0); setError(null);
  };

  const checkout = async () => {
    setError(null);
    if (cart.length === 0) {
      swalWarning('Cart is empty', 'Add at least one product before charging.');
      return;
    }
    if (paid + 0.0001 < total) {
      swalWarning('Payment short', `You're short by ${fmtMoney(due)}. Add or adjust a payment to cover the total.`);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        branch_id: me?.branch_id,
        customer_id: customerId || null,
        discount_amount: Number(discount) || 0,
        items: cart.map((r) => ({ product_id: r.product_id, qty: r.qty, unit_price: r.unit_price, discount: 0 })),
        payments: payments
          .filter((p) => Number(p.amount) > 0)
          .map((p) => ({ method: p.method, amount: Number(p.amount), reference: p.reference || null })),
      };
      const res = await axios.post(salesUrl, payload);
      if (res.data?.success) {
        const full = await axios.get(saleByIdUrl(res.data.saleId));
        if (full.data?.success) {
          setLastSale({
            sale: full.data.sale, items: full.data.items, payments: full.data.payments,
            branchName: full.data.sale.branch_name, cashier: me?.full_name,
          });
        }
        publishThankYou({ total, change, receiptNo: full.data?.sale?.receipt_no });
        reset(); await load();
      } else {
        swalError('Sale failed', res.data?.message || 'The sale could not be saved. Please try again.');
      }
    } catch (err) {
      swalError('Sale failed', err.response?.data?.message || 'A network or server error occurred. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="pos-shell">
        <motion.div
          className="pos-brand-bar"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <div className="pos-brand-left">
            {facility?.logo ? (
              <img src={`${ASSETS}${facility.logo}`} alt="logo" className="pos-logo" />
            ) : (
              <div className="pos-logo placeholder">{(facility?.name || 'P').slice(0, 1)}</div>
            )}
            <div className="pos-brand-text">
              <h1>{facility?.name || 'Point of Sale'}</h1>
              <span>{facility?.location || facility?.business_type || 'Live register'}</span>
            </div>
          </div>
          <div className="pos-brand-right">
            <div className="pos-cashier">
              <span className="lbl">Cashier</span>
              <span className="val">{me?.full_name || 'Unknown'}</span>
            </div>
            <motion.div
              className="pos-cart-pill"
              animate={cartCount > 0 ? { scale: [1, 1.04, 1] } : {}}
              transition={{ duration: 0.3 }}
              key={cartCount}
            >
              <span>🛒</span>
              <strong>{cartCount}</strong>
              <span className="amt">{fmtMoney(total)}</span>
            </motion.div>
          </div>
        </motion.div>

        <div className="pos-wrap">
          <div className="pos-left">
            {/* SEARCH BAR — input + camera-scan button + F2 hint */}
            <div className="pos-search">
              <span className="pos-search-ic">🔍</span>
              <input
                ref={searchRef} autoFocus
                placeholder="Scan barcode or search product (⌘K)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const code = search.trim();
                    if (!code) return;
                    // If it looks like a barcode (mostly digits), do exact lookup;
                    // otherwise add the first filtered result.
                    if (/^\d{6,}$/.test(code)) {
                      handleScannedCode(code);
                    } else if (filtered.length > 0) {
                      addToCart(filtered[0]);
                      setSearch('');
                    }
                  }
                  if (e.key === 'Escape') { setSearch(''); }
                }}
              />
              <button
                type="button"
                className="pos-scan-btn"
                onClick={() => setScannerOpen(true)}
                title="Open camera barcode scanner (F2)"
              >
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M3 4v2M5 4v12M8 4v12M11 4v8M11 14v2M14 4v12M17 4v2M17 14v2"/>
                </svg>
                <span>Scan</span>
              </button>
              <button
                type="button"
                className="pos-scan-btn"
                onClick={() => window.open('/customer-display', 'cfd', 'width=1280,height=800')}
                title="Open the customer-facing display on the rear monitor — drag it there and press F11 for fullscreen"
              >
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <rect x="2" y="4" width="16" height="10" rx="1.5"/><path d="M7 17h6M10 14v3"/>
                </svg>
                <span>Rear display</span>
              </button>
            </div>

            {/* CATEGORY CHIPS */}
            {categories.length > 1 && (
              <div className="pos-chips">
                {categories.map(([name, count]) => (
                  <button
                    key={name}
                    className={`pos-chip ${activeCategory === name ? 'active' : ''}`}
                    onClick={() => setActiveCategory(name)}
                  >
                    {name === 'all' ? 'All' : name}
                    <span className="ct">{count}</span>
                  </button>
                ))}
              </div>
            )}

            {/* PRODUCT GRID */}
            <div className="pos-products">
              {filtered.length === 0 ? (
                <div className="pos-empty" style={{ gridColumn: '1 / -1' }}>
                  <div className="ic">📭</div>
                  <h3>No products to show</h3>
                  <p>
                    {inventory.length === 0
                      ? 'Your branch has no stock yet. Add products + opening stock first.'
                      : 'Nothing matches that search. Try a different keyword or clear filters.'}
                  </p>
                </div>
              ) : (
                <AnimatePresence>
                  {filtered.map((p, idx) => {
                    const oos = Number(p.qty_available) <= 0;
                    const inCart = cartQty.get(p.product_id) || 0;
                    const imgSrc = `${ASSETS}${p.image || defaultImageFor(p)}`;
                    return (
                      <motion.button
                        key={p.product_id}
                        layout
                        initial={{ opacity: 0, y: 8 }}
                        animate={{
                          opacity: 1, y: 0,
                          scale: bumpedId === p.product_id ? [1, 1.06, 1] : 1,
                        }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.25, delay: idx < 16 ? idx * 0.015 : 0 }}
                        whileHover={oos ? {} : { y: -3 }}
                        whileTap={oos ? {} : { scale: 0.97 }}
                        className={`pos-tile ${oos ? 'oos' : ''} ${inCart ? 'in-cart' : ''}`}
                        onClick={() => addToCart(p)}
                        disabled={oos}
                      >
                        <div className="thumb">
                          <img
                            src={imgSrc}
                            alt=""
                            onError={(e) => { e.target.src = `${ASSETS}/uploads/products/defaults/generic.svg`; }}
                          />
                          {!oos && <span className="add-chip">+</span>}
                          {inCart > 0 && (
                            <span className="in-cart-badge">{inCart} in cart</span>
                          )}
                        </div>
                        <div className="name">{p.name}</div>
                        <div className="price">{fmtMoney(p.effective_price ?? p.selling_price)}</div>
                        <div className="stock">
                          {oos
                            ? <span className="oos-tag">Out of stock</span>
                            : <>{Number(p.qty_available)} {p.unit || ''} available</>}
                        </div>
                      </motion.button>
                    );
                  })}
                </AnimatePresence>
              )}
            </div>
          </div>

          {/* RIGHT — cart + checkout */}
          <div className="pos-right">
            <div className="pos-cart-head">
              <div>
                <h3>Current sale</h3>
                <div className="pos-cart-sub">{cartCount > 0 ? `${cartCount} item${cartCount === 1 ? '' : 's'}` : 'No items yet'}</div>
              </div>
              <select
                className="f-select pos-customer-select"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
              >
                <option value="">🚶 Walk-in customer</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.full_name}{c.phone ? ` · ${c.phone}` : ''}</option>)}
              </select>
            </div>

            <div className="pos-cart">
              {cart.length === 0 ? (
                <motion.div
                  className="pos-empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <motion.div
                    className="ic"
                    animate={{ y: [0, -6, 0] }}
                    transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
                  >🛒</motion.div>
                  <h3>Cart is empty</h3>
                  <p>Tap a tile, scan a barcode (USB), or click <strong>Scan</strong> to use the camera.</p>
                </motion.div>
              ) : (
                <AnimatePresence initial={false}>
                  {cart.map((r) => (
                    <motion.div
                      key={r.product_id}
                      layout
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20, height: 0, paddingTop: 0, paddingBottom: 0, marginTop: 0 }}
                      transition={{ duration: 0.22 }}
                      className="pos-row-v2"
                    >
                      <img
                        src={`${ASSETS}${r.image || defaultImageFor(r)}`}
                        alt=""
                        className="pos-row-thumb"
                        onError={(e) => { e.target.src = `${ASSETS}/uploads/products/defaults/generic.svg`; }}
                      />
                      <div className="pos-row-info">
                        <div className="nm">{r.name}</div>
                        <div className="meta">{fmtMoney(r.unit_price)} × {r.qty} {r.unit || ''}</div>
                        <div className="pos-qty">
                          <button onClick={() => setQty(r.product_id, r.qty - 1)} aria-label="Decrease">−</button>
                          <input
                            value={r.qty}
                            onChange={(e) => setQty(r.product_id, e.target.value)}
                            aria-label="Quantity"
                          />
                          <button onClick={() => setQty(r.product_id, r.qty + 1)} aria-label="Increase">+</button>
                        </div>
                      </div>
                      <div className="pos-row-actions">
                        <div className="lt">{fmtMoney(r.qty * r.unit_price)}</div>
                        <button className="pos-row-remove" onClick={() => removeRow(r.product_id)} title="Remove from cart" aria-label="Remove">
                          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 6h12M8 6V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2M6 6l1 10a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1l1-10"/>
                          </svg>
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </div>

            {/* TOTALS + PAYMENT */}
            <div className="pos-totals">
              <div className="ln"><span>Subtotal</span><span>{fmtMoney(subtotal)}</span></div>
              <div className="ln">
                <span>Discount (GHS)</span>
                <input
                  className="pos-discount-input"
                  type="number" step="0.01" value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="ln total"><span>Total</span><span>{fmtMoney(total)}</span></div>

              {/* Payment method pills */}
              <div className="pos-pay-methods">
                {PAYMENT_METHODS.map((m) => {
                  const active = payments[activePay]?.method === m.key;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      className={`pos-pay-method ${m.color} ${active ? 'active' : ''}`}
                      onClick={() => setPayment(activePay, { method: m.key })}
                    >
                      <span className="ic">{m.icon}</span>
                      <span className="lbl">{m.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Payment row(s) */}
              {payments.map((p, i) => {
                const meta = PAYMENT_METHODS.find((m) => m.key === p.method) || PAYMENT_METHODS[0];
                return (
                  <div
                    key={i}
                    className={`pos-pay-row ${i === activePay ? 'active' : ''}`}
                    onClick={() => setActivePay(i)}
                  >
                    <span className="pos-pay-tag">{meta.icon} {meta.label}</span>
                    <input
                      type="number" step="0.01" placeholder="0.00"
                      value={p.amount}
                      onChange={(e) => setPayment(i, { amount: e.target.value })}
                      onFocus={() => setActivePay(i)}
                    />
                    {payments.length > 1 && (
                      <button className="pos-pay-rm" onClick={(e) => { e.stopPropagation(); removePayment(i); }} title="Remove">×</button>
                    )}
                  </div>
                );
              })}

              {/* Quick-cash chips */}
              {payments[activePay]?.method === 'cash' && cart.length > 0 && (
                <div className="pos-cash-chips">
                  {QUICK_CASH.map((amt) => (
                    <button key={amt} type="button" className="pos-cash-chip" onClick={() => bumpCash(amt)}>
                      +GHS {amt}
                    </button>
                  ))}
                  <button type="button" className="pos-cash-chip exact" onClick={setExact} title="Set to the exact remaining due">
                    = Exact
                  </button>
                </div>
              )}

              <div className="pos-pay-foot">
                <button className="btn-link" onClick={addPayment}>+ Split payment</button>
                <div className={`pos-pay-status ${due > 0 ? 'due' : change > 0 ? 'change' : 'ok'}`}>
                  {due > 0
                    ? <>Due <strong>{fmtMoney(due)}</strong></>
                    : change > 0
                      ? <>Change <strong>{fmtMoney(change)}</strong></>
                      : 'Paid in full'}
                </div>
              </div>

              {error && <div className="notice" style={{ marginTop: 10 }}>{error}</div>}

              <div className="pos-actions">
                <button className="btn-ghost" onClick={reset} disabled={busy || cart.length === 0}>Clear</button>
                <motion.button
                  className="btn-primary pos-charge-btn"
                  onClick={checkout}
                  disabled={busy || cart.length === 0 || due > 0}
                  whileTap={{ scale: 0.97 }}
                >
                  {busy ? 'Processing…' : (
                    <>
                      <span>Charge</span>
                      <strong>{fmtMoney(total)}</strong>
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Receipt modal — appears after a successful sale */}
      <Modal
        title="Sale complete" open={!!lastSale} onClose={() => setLastSale(null)} width={420}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setLastSale(null)}>Close</button>
            <button className="btn-primary" onClick={() => window.print()}>🖨️ Print receipt</button>
          </>
        }
      >
        {lastSale && (
          <Receipt
            sale={lastSale.sale} items={lastSale.items} payments={lastSale.payments}
            facility={facility} branchName={lastSale.branchName} cashier={lastSale.cashier}
          />
        )}
      </Modal>

      {/* Camera barcode scanner */}
      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onResult={(code) => handleScannedCode(code)}
      />
    </>
  );
};

export default POS;
